import { useEffect, useState } from "react";

const EVENT = "store:subscription_inactive";
const SESSION_FLAG = "__nx_subscription_locked";

type LockoutDetail = {
  status: string;
  message: string;
};

let installed = false;

/**
 * Wraps window.fetch once at module load. Any /api/* response with HTTP 503
 * and a JSON body containing `{ error: "subscription_inactive" }` triggers
 * a global custom event which the <SubscriptionGate> overlay listens for.
 *
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export function installSubscriptionFetchInterceptor(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const res = await originalFetch(input, init);
    try {
      if (res.status !== 503) return res;
      // Prefer res.url (the final resolved URL after redirects). Fall back to
      // the request input. Request#toString() returns "[object Request]" so
      // we must check the input type carefully.
      let url = res.url || "";
      if (!url) {
        if (typeof input === "string") url = input;
        else if (input instanceof URL) url = input.toString();
        else if (typeof Request !== "undefined" && input instanceof Request) url = input.url;
      }
      if (!url || !url.includes("/api/")) return res;
      // Clone so the caller can still consume the body normally.
      const cloned = res.clone();
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) return res;
      const body = await cloned.json().catch(() => null);
      if (body && typeof body === "object" && body.error === "subscription_inactive") {
        try { sessionStorage.setItem(SESSION_FLAG, "1"); } catch {}
        window.dispatchEvent(
          new CustomEvent<LockoutDetail>(EVENT, {
            detail: {
              status: String(body.status || "inactive"),
              message: String(body.message || "هذا المتجر معلق حالياً."),
            },
          }),
        );
      }
    } catch {
      // Never let interceptor errors break the original request.
    }
    return res;
  };
}

/**
 * Full-screen Arabic lockout overlay shown when the API reports the
 * tenant's subscription is inactive (suspended / past_due / cancelled).
 * Mounted once at the App root.
 */
export function SubscriptionGate() {
  const [restoredFromSession] = useState<boolean>(() => {
    try {
      return typeof window !== "undefined" && sessionStorage.getItem(SESSION_FLAG) === "1";
    } catch {
      return false;
    }
  });
  const [detail, setDetail] = useState<LockoutDetail | null>(() =>
    restoredFromSession
      ? { status: "inactive", message: "هذا المتجر معلق حالياً. يرجى التواصل مع إدارة المنصة." }
      : null,
  );

  useEffect(() => {
    const onLock = (e: Event) => {
      const d = (e as CustomEvent<LockoutDetail>).detail;
      setDetail(d || { status: "inactive", message: "هذا المتجر معلق حالياً." });
    };
    window.addEventListener(EVENT, onLock);
    return () => window.removeEventListener(EVENT, onLock);
  }, []);

  // Self-healing: when the overlay was restored from sessionStorage (previous
  // page view saw a 503), re-verify against the API. If the store has been
  // reactivated since, clear the flag and drop the overlay automatically —
  // otherwise merchants/customers stay locked on a stale "suspended" screen
  // until they manually press إعادة المحاولة.
  useEffect(() => {
    if (!restoredFromSession || typeof window === "undefined") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/lcp-hint", { cache: "no-store", credentials: "include" });
        if (cancelled) return;
        if (res.status !== 503) {
          try { sessionStorage.removeItem(SESSION_FLAG); } catch {}
          setDetail((d) => (d && d.status === "inactive" ? null : d));
        }
        // A real 503 re-fires the lockout event via the fetch interceptor,
        // which refreshes `detail` with the live status/message.
      } catch {
        // Network error: keep the overlay; the retry button still works.
      }
    })();
    return () => { cancelled = true; };
  }, [restoredFromSession]);

  // The inline index.html boot script paints a plain-DOM maintenance shell
  // (#__nx-suspend-shell) the instant /api/lcp-hint returns 503, before the
  // bundle loads. Remove it ONLY once this React gate is actually rendering
  // its own overlay — never on plain mount, otherwise a suspended store whose
  // sessionStorage read failed would lose the shell before the first API 503
  // fires the lockout event.
  useEffect(() => {
    if (!detail || typeof document === "undefined") return;
    const shell = document.getElementById("__nx-suspend-shell");
    if (shell) shell.remove();
  }, [detail]);

  if (!detail) return null;

  return (
    <div
      dir="rtl"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="nx-lockout-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 17, 21, 0.96)",
        color: "#fafafa",
        zIndex: 2147483000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px",
        fontFamily: "system-ui, -apple-system, 'Segoe UI', Tahoma, sans-serif",
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: 520, width: "100%" }}>
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: "rgba(255, 159, 28, 0.15)",
            color: "#ffb84d",
            margin: "0 auto 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 36,
            fontWeight: 700,
          }}
          aria-hidden="true"
        >
          ⏸
        </div>
        <h1 id="nx-lockout-title" style={{ fontSize: 28, marginBottom: 16, fontWeight: 700 }}>
          هذا المتجر معلق حالياً
        </h1>
        <p style={{ fontSize: 17, lineHeight: 1.7, marginBottom: 28, color: "#d4d4d8" }}>
          {detail.message}
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => {
              try { sessionStorage.removeItem(SESSION_FLAG); } catch {}
              window.location.reload();
            }}
            style={{
              padding: "12px 24px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent",
              color: "#fafafa",
              fontSize: 15,
              cursor: "pointer",
            }}
          >
            إعادة المحاولة
          </button>
          <a
            href={(import.meta.env.VITE_PLATFORM_URL as string | undefined)?.trim() || "https://nexuscart.co"}
            style={{
              padding: "12px 24px",
              borderRadius: 10,
              background: "#ffb84d",
              color: "#1a1a1a",
              fontSize: 15,
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            زيارة منصة نكسس كارت
          </a>
        </div>
        <p style={{ fontSize: 13, marginTop: 24, color: "#71717a" }}>
          الحالة: {detail.status}
        </p>
      </div>
    </div>
  );
}
